import apiClient from "@/api/apiClient";
import { ErrorCodes, ErrorResponseSchema, type ErrorResponse } from "@/api/backendErrors";
import { BACKEND_API_PATH, BACKEND_PUBLIC_API_PATH, BACKEND_URL_TRAILED } from "@/configs/backend";
import { isDefined, isDefinedString } from "@/utils/format";

import type { DataTableOptions } from "@/configs/datatable";

type HttpMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

interface RequestOptions extends Omit<RequestInit, "method" | "body"> {
  params?: BackendRequestParams;
  body?: unknown;
  /** `false` for unauthenticated routes (`/api/public`), no access token is sent. Default: `true` */
  authenticated?: boolean;
}

export class BackendResponseError extends Error implements ErrorResponse {
  status: number;
  errorCode: string;
  override cause?: unknown;

  constructor(response: ErrorResponse) {
    super(response.message, { cause: response.cause });
    this.name = "BackendResponseError";
    this.status = response.status;
    this.errorCode = response.errorCode;
    this.cause = response.cause;
  }
}

export type BackendResponseFailure = { data: null; count: number | null; error: BackendResponseError | Error };
export type BackendResponseSuccess<T> = { data: T; count: number | null; error: null };
export type BackendResponse<T> = BackendResponseSuccess<T> | BackendResponseFailure;

export type BackendRequestParams = Record<string, string | string[] | number | number[] | boolean | null | undefined>;

export async function getAccessToken(): Promise<string | null> {
  const { data, error } = await apiClient.auth.getSession();
  if (error) throw error;
  return data.session?.access_token ?? null;
}

// Maps mantine-datatables `page` (1-based) and `size` to the backend's
// `offset` and `limit`. page<=0 is treated as page 1.
export function transformPaginationParams(
  page: BackendRequestParams[string],
  size: BackendRequestParams[string],
): { limit?: number; offset?: number } {
  const sizeNum = typeof size === "number" ? size : undefined;
  const pageNum = typeof page === "number" ? page : undefined;

  if (!isDefined(sizeNum)) return {};
  if (!isDefined(pageNum)) return { limit: sizeNum };

  return { limit: sizeNum, offset: Math.max(0, pageNum - 1) * sizeNum };
}

export type HonoBackendPaginationProps = {
  limit?: number | undefined;
  offset?: number | undefined;
};
export type HonoBackendSortProps = {
  sort_by?: string | undefined;
  sort_dir?: string | undefined;
};
export type HonoBackendSearchProps = {
  search?: string | undefined;
};

export const parseBackendClientDataTableOptions = (
  dataTableOptions: Partial<DataTableOptions>,
): HonoBackendPaginationProps & HonoBackendSortProps & HonoBackendSearchProps => {
  const { limit, offset } = transformPaginationParams(dataTableOptions.page, dataTableOptions.size);
  return {
    limit,
    offset,
    search: dataTableOptions.search,
    sort_by: dataTableOptions.sortBy,
    sort_dir: dataTableOptions.sortDirection,
  };
};

export function buildUrlParams(params: BackendRequestParams): URLSearchParams {
  const { page, size, ...rest } = params;
  const searchParams = new URLSearchParams();

  for (const [key, value] of Object.entries(rest)) {
    // handles array values
    if (Array.isArray(value)) {
      if (value.length > 0) searchParams.set(key, value.join(","));
      continue;
    }
    // handles string values, eg. "search"
    if (typeof value === "string") {
      if (isDefinedString(value)) searchParams.set(key, value);
      continue;
    }
    // handles number, boolean, null values
    if (isDefined(value)) {
      searchParams.set(key, String(value));
    }
  }

  return searchParams;
}

export function buildUrl(path: string, params?: RequestOptions["params"], apiPath = BACKEND_API_PATH): string {
  const url = new URL(apiPath + path, BACKEND_URL_TRAILED);

  if (params) {
    url.search = buildUrlParams(params).toString();
  }

  return url.toString();
}

export async function parseErrorResponse(response: Response): Promise<BackendResponseError> {
  const raw: unknown = await response.json().catch(() => null);
  const parsed = ErrorResponseSchema.safeParse(raw);
  if (parsed.success) {
    return new BackendResponseError(parsed.data);
  }
  return new BackendResponseError({
    status: response.status,
    errorCode: ErrorCodes.INTERNAL_ERROR,
    message: response.statusText || "Unexpected error",
    cause: raw,
  });
}

/**
 * Example usage:
 * const { data, error } = await backendClient.get<MaintenanceTag[]>("/maintenance-tags", { params: { limit: 3 } });
 * if (error) throw error;
 */
async function request<T>(method: HttpMethod, path: string, options: RequestOptions = {}): Promise<BackendResponse<T>> {
  const { params, body, headers: extraHeaders, authenticated = true, ...rest } = options;

  const isFormData = body instanceof FormData;
  const headers: Record<string, string> = {
    // FormData sets its own multipart Content-Type (incl. boundary)
    ...(isFormData ? {} : { "Content-Type": "application/json" }),
    ...(extraHeaders as Record<string, string>),
  };

  if (authenticated) {
    const accessToken = await getAccessToken();
    if (!accessToken) throw new Error("No access token found");
    headers.Authorization = `Bearer ${accessToken}`;
  }

  const url = buildUrl(path, params, authenticated ? BACKEND_API_PATH : BACKEND_PUBLIC_API_PATH);

  try {
    const response = await fetch(url, {
      method,
      headers,
      body: isFormData ? body : body !== undefined ? JSON.stringify(body) : undefined,
      ...rest,
    });

    if (!response.ok) {
      return { data: null, count: null, error: await parseErrorResponse(response) };
    }

    const contentType = response.headers.get("Content-Type") ?? "";

    if (contentType.includes("application/json")) {
      const responseData: BackendResponseSuccess<T> = (await response.json()) as BackendResponseSuccess<T>;
      return { data: responseData.data, count: responseData.count, error: null };
    }

    const responseData: T = (await response.text()) as T;
    return { data: responseData, count: null, error: null };
  } catch (err) {
    return {
      data: null,
      count: null,
      error: err instanceof Error ? err : new Error(String(err)),
    };
  }
}

export const backendClient = {
  get: <T>(path: string, options?: RequestOptions) => request<T>("GET", path, options),
  post: <T>(path: string, options?: RequestOptions) => request<T>("POST", path, options),
  put: <T>(path: string, options?: RequestOptions) => request<T>("PUT", path, options),
  patch: <T>(path: string, options?: RequestOptions) => request<T>("PATCH", path, options),
  delete: <T>(path: string, options?: RequestOptions) => request<T>("DELETE", path, options),
};

export default backendClient;
